import styled, { css } from 'styled-components';

import colors from 'app/styles/theme/colors';
import { fontSizes } from 'app/styles/theme/sizes';

import type { NewTierPlansGridDensity } from './types';

const { gray, green, blue } = colors.designSystem;

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const Notice = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border-radius: 7px;
  border: 1px solid ${blue[500]};
  background-color: ${blue[100]};
  color: ${blue[900]};

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`;

export const NoticeTitle = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
`;

export const NoticeDescription = styled.p`
  margin: 2px 0 0;
  font-size: 13px;
  opacity: 0.85;
`;

export const Grid = styled.div<{ $density?: NewTierPlansGridDensity; $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, minmax(0, 1fr));
  gap: ${({ $density }) => ($density === 'comfortable' ? '20px' : '12px')};
  margin-top: ${({ $density }) => ($density === 'comfortable' ? '16px' : '0')};

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const Badge = styled.span`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 10px;
  border-radius: 999px;
  background-color: ${green[500]};
  color: ${gray[0]};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const CurrentPlanBadge = styled.span`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 10px;
  border-radius: 999px;
  background-color: ${gray[700]};
  color: ${gray[0]};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const NameBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const PlanTitle = styled.p`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
`;

export const PlanPricePeriod = styled.span<{ $density?: NewTierPlansGridDensity }>`
  font-size: ${({ $density }) => ($density === 'comfortable' ? '14px' : '13px')};
  font-weight: 400;
  color: ${gray[700]};
`;

export const PlanPrice = styled.p<{ $density?: NewTierPlansGridDensity }>`
  margin: 0;
  font-size: ${({ $density }) => ($density === 'comfortable' ? '32px' : '20px')};
  font-weight: 700;
  line-height: 1;
`;

export const PlanTagline = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${gray[700]};
`;

export const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const FeatureRow = styled.div<{ $enabled: boolean; $inverse?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${({ $enabled }) => ($enabled ? 'inherit' : gray[500])};

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    color: ${({ $enabled, $inverse }) => {
      if (!$enabled) return gray[500];
      return $inverse ? green[400] : green[500];
    }};
  }
`;

export const PlanCta = styled.button<{ $inverse?: boolean }>`
  margin-top: auto;
  width: 100%;
  padding: 14px 20px;
  border-radius: 7px;
  border: 1px solid transparent;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  background-color: ${green[500]};
  color: ${gray[0]};

  @media (prefers-reduced-motion: no-preference) {
    transition: background-color 0.15s ease;
  }

  &:hover,
  &:focus-visible {
    background-color: ${green[700]};
  }

  ${({ $inverse }) =>
    $inverse &&
    css`
      background-color: ${gray[0]};
      color: ${gray[950]};

      &:hover,
      &:focus-visible {
        background-color: ${gray[200]};
      }
    `}

  &:disabled {
    cursor: not-allowed;
    background-color: ${gray[200]};
    color: ${gray[700]};
  }

  &:disabled:hover,
  &:disabled:focus-visible {
    background-color: ${gray[200]};
  }
`;

export const PlanCard = styled.div<{
  $density?: NewTierPlansGridDensity;
  $highlighted?: boolean;
  $inverse?: boolean;
}>`
  position: relative;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: ${({ $density }) => ($density === 'comfortable' ? '24px' : '16px')};
  border-radius: 7px;
  border: 1px solid ${gray[200]};
  background-color: ${gray[0]};
  color: ${gray[950]};

  ${({ $highlighted }) =>
    $highlighted &&
    css`
      border: 2px solid ${green[500]};
      box-shadow: 0 0 0 4px ${green[100]};
    `}

  ${({ $inverse }) =>
    $inverse &&
    css`
      background-color: ${gray[900]};
      color: ${gray[0]};

      ${PlanTagline} {
        color: ${gray[0]};
      }

      ${PlanPricePeriod} {
        color: ${gray[400]};
      }
    `}
`;

export const PersonalizedBanner = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 32px;
  padding: 24px;
  border-radius: 7px;
  border: 1px solid ${green[200]};
  background-color: ${green[25]};
  color: ${gray[950]};

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
    gap: 24px;
  }
`;

export const PersonalizedContent = styled.div`
  flex: 1 1 0;
  min-width: 280px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 600px) {
    min-width: 0;
  }
`;

export const PersonalizedTitle = styled.p`
  margin: 0;
  font-size: ${fontSizes.lg};
  font-weight: 600;
  line-height: 1.2;
`;

export const PersonalizedSubtitle = styled.p`
  margin: 0;
  font-size: ${fontSizes.sm};
  color: ${gray[700]};
`;

export const PersonalizedItems = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, max-content));
  gap: 8px 32px;
  font-size: 13px;
  color: ${gray[800]};

  @media (max-width: 600px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const PersonalizedItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    color: ${green[500]};
  }
`;

export const PersonalizedAction = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  @media (max-width: 600px) {
    align-items: stretch;
    text-align: center;
  }
`;

export const PersonalizedCta = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 36px;
  padding: 0 16px;
  border-radius: 7px;
  border: 1px solid ${green[500]};
  background-color: ${gray[0]};
  color: ${green[700]};
  font-size: ${fontSizes.sm};
  font-weight: 500;
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
  }

  @media (prefers-reduced-motion: no-preference) {
    transition: background-color 0.15s ease;
  }

  &:hover,
  &:focus-visible {
    background-color: ${green[100]};
  }

  &:focus-visible {
    outline: 2px solid ${green[700]};
    outline-offset: 2px;
  }
`;

export const PersonalizedHint = styled.span`
  font-size: 13px;
  color: ${gray[700]};
`;
